import type {
  FavouriteProductActionAPIResponse,
  FavouriteProductsAPIResponse,
  FavouriteProductsResponseAPIResponse,
} from './types'
import type { FavouriteProduct, FavouritesApiClient, ProductParameters } from '@/scopes/favourites/domain/favouriteTypes'
import defaultFetch from 'isomorphic-unfetch'

import { stringify } from 'query-string'
import { partialRight } from 'ramda'
import { createCustomError } from '@/scopes/common/infrastructure/logging/create-custom-error'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  queryStringSerializer: {
    serialize: typeof stringify
  }
}

export const ProductAPISystemError = createCustomError('ProductAPISystemError')
export const ProductAPIUserNotIdentifiedError = createCustomError('ProductAPIUserNotIdentifiedError')
export const ProductAPIUserUnexpectedError = createCustomError('ProductAPIUserUnexpectedError')

export function createFavouritesApiClient(dependencies: Partial<Dependencies> = {}): FavouritesApiClient {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = { serialize: partialRight(stringify, [{ arrayFormat: 'index' }]) },
  } = dependencies as Dependencies

  async function getFavouriteProducts(): Promise<string[]> {
    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/ProductFavourites/GetFavourites`

    const response = await fetchWithOptions(url, 'Failed to retrieve favourites due to system error')

    const { accoIds } = (await response.json()) as FavouriteProductsAPIResponse

    return accoIds.map(String)
  }

  async function getFavouriteProductsResponse(contextItemId: string): Promise<FavouriteProduct[]> {
    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/ProductFavourites/GetFavouritesResponse?contextitemid=${encodeURIComponent(contextItemId)}`
    const response = await fetchWithOptions(url, 'Failed to retrieve favourites response due to system error')
    const data: unknown = await response.json()

    if (!isFavouriteProductsResponse(data)) {
      throw new ProductAPIUserUnexpectedError('Failed to retrieve favourites, unexpected response')
    }

    return data.results
  }

  async function addProductToFavourites({ contextItemId, productId }: ProductParameters): Promise<void> {
    const queryString = queryStringSerializer.serialize({
      contextItemId,
      accommodationId: productId,
    })

    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/ProductFavourites/AddFavourite?${queryString}`

    const response = await fetchWithOptions(url, 'Failed to add favourite due to system error')

    await handleFavouriteActionResponse(response)
  }

  async function removeProductFromFavourites({ contextItemId, productId }: ProductParameters): Promise<void> {
    const queryString = queryStringSerializer.serialize({
      contextItemId,
      accommodationId: productId,
    })

    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/ProductFavourites/RemoveFavourite?${queryString}`

    const response = await fetchWithOptions(url, 'Failed to remove favourite due to system error')

    await handleFavouriteActionResponse(response)
  }

  async function removeAllFavourites(): Promise<void> {
    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/ProductFavourites/RemoveAllFavourites`
    const response = await fetchWithOptions(url, 'Failed to remove all favourites due to system error')

    await handleFavouriteActionResponse(response)
  }

  async function fetchWithOptions(url: string, errorMessage: string) {
    const response = await fetch(url, {
      referrerPolicy: 'no-referrer-when-downgrade',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })

    responseOkOrThrow(response, errorMessage)

    return response
  }

  async function handleFavouriteActionResponse(response: Response) {
    const data = await response.json()

    userIdentifiedOrThrow(data)
    actionSuccessOrThrow(data)
  }

  function responseOkOrThrow(response: Response, errorMessage: string) {
    if (!response.ok) {
      throw new ProductAPISystemError(errorMessage)
    }
  }

  function userIdentifiedOrThrow({ success, isUserLoggedIn }: FavouriteProductActionAPIResponse) {
    if (!success && !isUserLoggedIn) {
      throw new ProductAPIUserNotIdentifiedError('Failed to add favourite, user not logged in')
    }
  }

  function actionSuccessOrThrow({ success }: FavouriteProductActionAPIResponse) {
    if (!success) {
      throw new ProductAPIUserUnexpectedError('Failed to add favourite, unexpected error')
    }
  }

  function isFavouriteProductsResponse(data: unknown): data is FavouriteProductsResponseAPIResponse {
    return typeof data === 'object'
      && data !== null
      && 'results' in data
      && Array.isArray(data.results)
  }

  return {
    getFavouriteProducts,
    getFavouriteProductsResponse,
    removeAllFavourites,
    addProductToFavourites,
    removeProductFromFavourites,
  }
}
